import React, { useState, useCallback, useRef } from 'react';
import { useZxing } from 'react-zxing';
import styles from './index.module.scss';

interface MobileBarcodeScannerProps {
	isOpen: boolean;
	onClose: () => void;
	onScanSuccess?: (barcode: string) => void;
}

const SCAN_COOLDOWN = 2000;

const beep = (): void => {
	try {
		const ctx = new (
			window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
		)();
		const osc = ctx.createOscillator();
		const gain = ctx.createGain();
		osc.type = 'sine';
		osc.frequency.setValueAtTime(1046, ctx.currentTime);
		gain.gain.setValueAtTime(0.08, ctx.currentTime);
		gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
		osc.connect(gain);
		gain.connect(ctx.destination);
		osc.start();
		osc.stop(ctx.currentTime + 0.15);
	} catch {
		// silently fail if AudioContext not supported
	}
};

export const MobileBarcodeScanner: React.FC<MobileBarcodeScannerProps> = ({ isOpen, onClose, onScanSuccess }) => {
	const [lastScanned, setLastScanned] = useState<string | null>(null);
	const [flash, setFlash] = useState<boolean>(false);

	const videoRef = useRef<HTMLVideoElement>(null);

	const scanLockRef = useRef<boolean>(false);
	const lastScanTimeRef = useRef<number>(0);

	React.useEffect(() => {
		if (!isOpen) {
			scanLockRef.current = false;
			lastScanTimeRef.current = 0;
		}
	}, [isOpen]);

	const handleDecode = useCallback(
		(result: string): void => {
			const now = Date.now();

			if (scanLockRef.current || now - lastScanTimeRef.current < SCAN_COOLDOWN) return;

			scanLockRef.current = true;
			lastScanTimeRef.current = now;

			beep();
			navigator.vibrate?.(80);

			setFlash(true);
			setTimeout(() => setFlash(false), 350);

			setLastScanned(result);
			onScanSuccess?.(result);

			setTimeout(() => {
				onClose();
				setLastScanned(null);
				scanLockRef.current = false;
			}, 900);
		},
		[onScanSuccess, onClose]
	);

	const { ref: zxingRef } = useZxing({
		paused: !isOpen,
		constraints: {
			video: {
				facingMode: { ideal: 'environment' },
				width: { ideal: 1280 },
				height: { ideal: 720 },
			},
		},
		timeBetweenDecodingAttempts: 150,
		onDecodeResult(result) {
			handleDecode(result.getText());
		},
		onError(err) {
			console.error('[BarcodeScanner] decode error:', err);
		},
	});

	const mergedRef = useCallback(
		(node: HTMLVideoElement | null) => {
			(videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = node;
			(zxingRef as React.MutableRefObject<HTMLVideoElement | null>).current = node;
		},
		[zxingRef]
	);

	if (!isOpen) return null;

	return (
		<div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Barcode scanner">
			<div className={`${styles.successFlash} ${flash ? styles.successFlashActive : ''}`} aria-hidden="true" />

			<video
				ref={mergedRef}
				className={styles.video}
				autoPlay
				playsInline
				muted
				aria-label="Camera feed for barcode scanning"
			/>

			<div className={styles.scanWindow} aria-hidden="true">
				<div className={styles.scanLine} />
				<span className={`${styles.corner} ${styles.cornerTL}`} />
				<span className={`${styles.corner} ${styles.cornerTR}`} />
				<span className={`${styles.corner} ${styles.cornerBL}`} />
				<span className={`${styles.corner} ${styles.cornerBR}`} />
			</div>

			<div className={styles.header}>
				<p className={styles.hint}>Point camera at barcode</p>
				<button className={styles.closeBtn} onClick={onClose} aria-label="Close scanner" type="button">
					✕
				</button>
			</div>

			<div className={`${styles.confirmBar} ${lastScanned ? styles.confirmBarVisible : ''}`}>
				<span className={styles.confirmLabel}>Scanned</span>
				<span className={styles.confirmValue}>{lastScanned}</span>
			</div>
		</div>
	);
};

export default MobileBarcodeScanner;
